import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowRight, Check, Compass, Info, Radar, Sparkles, TrendingUp, Users } from "lucide-react";

import { createReport, getPersonas } from "../api/persona";
import { useAuth } from "../contexts/AuthContext";
import { useUsage } from "../hooks/useUsage";
import LimitReachedDialog from "./LimitReachedDialog";
import PersonaLoaderDialog from "./PersonaLoaderDialog";
import UsageMeter from "./UsageMeter";

const EXAMPLE_PERSONAS = [
  { emoji: "💑", label: "Couples", score: 84 },
  { emoji: "👨‍👩‍👧", label: "Families", score: 61 },
  { emoji: "💼", label: "Business", score: 47 },
  { emoji: "🎒", label: "Solo", score: 72 },
];

const PersonaEngine = () => {
  const navigate = useNavigate();
  const { user, businessProfile } = useAuth();
  const [personas, setPersonas] = useState([]);
  const [selected, setSelected] = useState([]);
  const [reportName, setReportName] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [limitMessage, setLimitMessage] = useState(null);
  const { byFeature: usage, refresh: refreshUsage } = useUsage();

  const isProfileComplete = businessProfile?.is_complete;

  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    getPersonas()
      .then((r) => setPersonas(r.data || []))
      .catch(() => {});
  }, [userId]);

  const openAuth = () => navigate("?auth=login");
  const openBusinessProfile = () => window.dispatchEvent(new CustomEvent("openBusinessProfile"));

  const handleGenerate = () => {
    if (!user) {
      openAuth();
      return;
    }
    if (!isProfileComplete) {
      openBusinessProfile();
      return;
    }
    // Default: select all available personas (no cap).
    setSelected(personas.map((p) => p.id));
    setShowPicker(true);
  };

  const togglePersona = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleRunScan = async () => {
    if (!selected.length) return;
    setError(null);
    setLimitMessage(null);
    setScanning(true);
    try {
      const res = await createReport(selected, reportName.trim() || undefined);
      window.dispatchEvent(new CustomEvent("personaReportsUpdated"));
      navigate(`/persona/report/${res.data.report_id}`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403) {
        refreshUsage();
        setLimitMessage(detail || "You've reached your plan limit.");
        setScanning(false);
        return;
      }
      if (detail === "profile_required") {
        setError("Complete your business profile to generate a report.");
        openBusinessProfile();
      } else if (status === 422) {
        setError("Select at least one valid persona.");
      } else if (status === 503) {
        setError("No AI providers are available right now. Please try again later.");
      } else {
        setError((typeof detail === "string" ? detail : null) || "Failed to generate report.");
      }
      setScanning(false);
    }
  };

  const renderScanAction = () => {
    if (!user) {
      return (
        <button
          onClick={openAuth}
          className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-4 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 active:scale-[0.99]"
        >
          Log In to Run Scan
          <ArrowRight className="w-4 h-4" />
        </button>
      );
    }

    if (!isProfileComplete) {
      return (
        <button
          onClick={openBusinessProfile}
          className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-4 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 active:scale-[0.99]"
        >
          Complete Business Profile First
          <ArrowRight className="w-4 h-4" />
        </button>
      );
    }

    if (!showPicker) {
      return (
        <div className="space-y-3">
          <button
            onClick={handleGenerate}
            className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-4 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 active:scale-[0.99]"
          >
            <Radar className="w-4 h-4" />
            Generate Persona Report
          </button>
          {user && <UsageMeter item={usage?.persona_reports} className="justify-center" />}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <h3 className="font-headline text-base font-bold mb-1">Select Personas to Scan</h3>
          <p className="text-xs text-on-surface-variant mb-3">
            We&apos;ll measure how each traveler segment discovers{" "}
            <span className="text-on-surface font-medium">{businessProfile?.business_name}</span>{" "}
            across ChatGPT, Gemini &amp; Claude.
          </p>
          <input
            type="text"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            placeholder="Report name (optional)"
            className="w-full mb-3 px-3 py-2 rounded-xl bg-surface-container-lowest border border-outline-variant/30 text-sm text-on-surface placeholder:text-on-surface-variant focus:border-primary focus:outline-none"
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {personas.map((p) => {
              const isSelected = selected.includes(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => togglePersona(p.id)}
                  className={`relative flex flex-col items-center gap-1 p-3 rounded-xl border text-sm font-medium transition-all ${
                    isSelected
                      ? "bg-primary/10 border-primary text-primary"
                      : "bg-surface-container-lowest border-outline-variant/30 text-on-surface-variant hover:border-primary/50"
                  }`}
                >
                  {isSelected && (
                    <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-sm">
                      <Check className="w-3 h-3 text-on-primary" strokeWidth={3} />
                    </div>
                  )}
                  <span className="text-xl">{p.emoji}</span>
                  <span className="text-xs text-center leading-tight">{p.label}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-on-surface-variant">
            {selected.length} of {personas.length} personas selected
          </p>
        </div>

        {error && <p className="text-error text-sm font-medium">{error}</p>}

        <button
          onClick={handleRunScan}
          disabled={scanning || !selected.length}
          className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-4 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99]"
        >
          <Radar className="w-4 h-4" />
          Run Persona Visibility Scan
        </button>
      </div>
    );
  };

  return (
    <>
      {scanning && <PersonaLoaderDialog personaCount={selected.length} />}
      {limitMessage && !scanning && (
        <LimitReachedDialog message={limitMessage} onClose={() => setLimitMessage(null)} />
      )}
      <main className="pt-10 pb-24 px-6 md:px-12 max-w-8xl mx-auto">
        {/* Hero */}
        <section className="mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-label text-[10px] font-extrabold tracking-[0.15em] mb-6 uppercase">
                Persona Visibility Tracker
              </span>

              <h1 className="font-headline text-5xl md:text-7xl font-extrabold tracking-tight text-on-surface mb-8 leading-[1.05]">
                Does AI Recommend You to the Right Travelers?
              </h1>

              <p className="font-body text-xl text-on-surface-variant max-w-2xl mb-6 leading-relaxed">
                Every traveler segment asks AI differently — couples, families, business travellers,
                solo explorers. We run persona-specific queries across ChatGPT, Gemini, and Claude
                and score how visible your business is to each one.
              </p>

              <div className="flex flex-wrap gap-6 mb-12 text-sm text-on-surface-variant">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  <span>Per-persona visibility scores</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <span>Track gaps &amp; trends over time</span>
                </div>
              </div>

              {/* Scan box */}
              <div className="bg-surface-container-low rounded-2xl p-6 border border-outline/10">
                <div className="flex items-start gap-2 mb-4">
                  <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <p className="text-xs text-on-surface-variant leading-relaxed">
                    Persona scans use your business profile - no separate setup required. Make sure
                    your Business Profile is up to date and reflects your business.
                  </p>
                </div>
                {renderScanAction()}
                {user && (
                  <button
                    onClick={() => navigate("/persona/trends")}
                    className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all"
                  >
                    <TrendingUp className="w-4 h-4" />
                    See your persona trends
                  </button>
                )}
              </div>
            </div>

            {/* Right: example radar card */}
            <div className="lg:col-span-5">
              <div className="flex items-center gap-2 mb-3">
                <Compass className="w-4 h-4 text-outline" />
                <span className="text-xs text-on-surface-variant font-medium">
                  Example: visibility by traveler persona
                </span>
              </div>
              <div className="bg-surface-container-lowest rounded-2xl border border-surface-container-high p-6 space-y-3">
                {EXAMPLE_PERSONAS.map((p) => (
                  <div key={p.label} className="flex items-center gap-3">
                    <span className="text-2xl">{p.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{p.label}</div>
                      <div className="mt-1.5 h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            p.score >= 60
                              ? "bg-emerald-500"
                              : p.score >= 50
                                ? "bg-yellow-500"
                                : "bg-orange-500"
                          }`}
                          style={{ width: `${p.score}%` }}
                        />
                      </div>
                    </div>
                    <div
                      className={`text-lg font-extrabold font-headline shrink-0 ${
                        p.score >= 60
                          ? "text-emerald-400"
                          : p.score >= 50
                            ? "text-yellow-400"
                            : "text-orange-400"
                      }`}
                    >
                      {p.score}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-on-surface-variant/60 text-center mt-2">
                Example data — your report will show real results
              </p>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="mb-12">
          <h2 className="font-headline text-3xl font-bold text-on-surface mb-12 text-center">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Users className="w-6 h-6 text-primary" />,
                title: "Pick Your Personas",
                desc: "Choose the traveler segments that matter to your property — couples, families, business, solo, and more.",
              },
              {
                icon: <Sparkles className="w-6 h-6 text-primary" />,
                title: "We Query Three AIs",
                desc: "For each persona we run discovery and validation prompts across ChatGPT, Gemini, and Claude.",
              },
              {
                icon: <Radar className="w-6 h-6 text-primary" />,
                title: "Score & Diagnose Gaps",
                desc: "Get a visibility score per persona, drill into why, and track how it changes over time.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-surface-container-low rounded-2xl p-8 border border-outline/10"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="font-headline text-xl font-bold text-on-surface mb-2">
                  {item.title}
                </h3>
                <p className="text-on-surface-variant text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </>
  );
};

export default PersonaEngine;
